import { useState, useEffect, useRef } from 'react';

/**
 * Threaded notes (EVP or finance). Each user has at most one entry,
 * which they can edit. Other users' notes render read-only.
 *
 * Props:
 *   notes         array of { id, userId, authorName, comment, updatedAt }
 *   currentUserId UUID of the logged-in user (to find "my" note)
 *   canEdit       whether the current user can author a note
 *   onSave        (comment: string) => void   debounced auto-save
 *   theme         { bg, border, label, sub, accent, text }
 *   title         string
 *   description   string
 *   placeholder   string
 *   emptyText     shown to read-only viewers when there are no notes
 */
export default function NotesThread({
  notes = [],
  currentUserId,
  canEdit,
  onSave,
  theme,
  title,
  description,
  placeholder,
  emptyText,
}) {
  const myNote = notes.find((n) => n.userId === currentUserId);
  const others = notes.filter((n) => n.userId !== currentUserId);

  const [draft, setDraft] = useState(myNote?.comment || '');
  const [saved, setSaved] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => { setDraft(myNote?.comment || ''); }, [myNote?.id, myNote?.updatedAt]);

  function handleChange(e) {
    const val = e.target.value;
    setDraft(val);
    setSaved(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSave(val);
      setSaved(true);
    }, 800);
  }

  return (
    <div style={{ ...s.wrap, background: theme.bg, borderColor: theme.border }}>
      <div style={s.header}>
        <div>
          <div style={{ ...s.label, color: theme.label }}>{title}</div>
          <div style={{ ...s.sub, color: theme.sub }}>{description}</div>
        </div>
        {canEdit && (
          <span style={{ ...s.saveStatus, color: saved ? '#16A34A' : '#9CA3AF' }}>
            {saved ? '✓ Saved' : 'Saving…'}
          </span>
        )}
      </div>

      {others.map((n) => (
        <div key={n.id} style={{ ...s.noteCard, borderColor: theme.border }}>
          <div style={{ ...s.noteAuthor, color: theme.label }}>
            {n.authorName}
            <span style={{ ...s.noteWhen, color: theme.sub }}>
              {' · '}{new Date(n.updatedAt).toLocaleString()}
            </span>
          </div>
          <div style={{ ...s.noteBody, color: theme.text }}>{n.comment}</div>
        </div>
      ))}

      {!canEdit && others.length === 0 && !myNote && (
        <div style={{ ...s.empty, color: theme.sub }}>{emptyText}</div>
      )}

      {canEdit && (
        <div style={{ ...s.draftWrap, borderColor: theme.border }}>
          <div style={{ ...s.draftLabel, color: theme.label }}>Your note</div>
          <textarea
            style={{ ...s.textarea, borderColor: theme.border, color: theme.text }}
            rows={3}
            value={draft}
            onChange={handleChange}
            placeholder={placeholder}
            maxLength={5000}
          />
          <div style={{ ...s.charCount, color: theme.sub }}>{draft.length} / 5,000</div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:       { border: '1px solid', borderRadius: '10px', padding: '16px 20px', marginTop: '12px', marginBottom: '8px' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  label:      { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '3px' },
  sub:        { fontSize: '12px' },
  saveStatus: { fontSize: '11px', fontWeight: 500, flexShrink: 0 },
  noteCard:   { background: '#fff', border: '1px solid', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' },
  noteAuthor: { fontSize: '11px', fontWeight: 600 },
  noteWhen:   { fontWeight: 400, fontStyle: 'italic' },
  noteBody:   { fontSize: '13px', lineHeight: 1.6, marginTop: '4px', whiteSpace: 'pre-wrap' },
  empty:      { fontSize: '12px', fontStyle: 'italic', padding: '8px 0' },
  draftWrap:  { marginTop: '6px', paddingTop: '12px', borderTop: '1px dashed' },
  draftLabel: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '4px' },
  textarea:   { width: '100%', border: '1px solid', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', background: '#fff', fontFamily: 'inherit', resize: 'vertical', minHeight: '70px', lineHeight: 1.6 },
  charCount:  { fontSize: '10px', textAlign: 'right', marginTop: '4px' },
};
